import React, { useEffect, useState } from 'react';

const SUPPORTED_CURRENCIES = ['USD', 'EUR', 'CAD', 'GBP'];
const currencySymbolMap = {
  USD: '$',
  EUR: '€',
  CAD: 'CA$',
  GBP: '£',
};

const InlineTextCell = ({ rowData, mixIns }) => {
  const field = mixIns?.field;
  const rawField = mixIns?.rawField;
  const currencyField = mixIns?.currencyField;
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(rowData?.[rawField] || rowData?.[field] || '');
  const [currency, setCurrency] = useState(
    (currencyField && rowData?.[currencyField]) || rowData?.currency || 'USD'
  );

  useEffect(() => {
    const incoming = rowData?.[rawField] || rowData?.[field] || '';
    const nextCurrency = (currencyField && rowData?.[currencyField]) || rowData?.currency || 'USD';
    setCurrency(nextCurrency);
    if (mixIns?.isAmount) {
      const numeric = Number(incoming);
      setValue(Number.isFinite(numeric) ? numeric.toFixed(2) : '');
    } else {
      setValue(incoming);
    }
  }, [rowData, rawField, field, mixIns, currencyField]);

  const saveAmount = (nextCurrency = currency) => {
    const numeric = Number(value);
    const normalized = Number.isFinite(numeric) ? numeric.toFixed(2) : '';
    if (typeof mixIns?.onSave === 'function') {
      mixIns.onSave(rowData._id, field, normalized, true, nextCurrency);
    }
    setValue(normalized);
    setEditing(false);
  };

  const onSave = (event) => {
    if (mixIns?.isAmount) {
      const relatedToCurrency =
        event?.relatedTarget && event.relatedTarget.dataset?.currencyControl === 'true';
      if (relatedToCurrency) {
        return;
      }
      saveAmount();
      return;
    }
    if (typeof mixIns?.onSave === 'function') {
      mixIns.onSave(rowData._id, field, value, false);
    }
    setEditing(false);
  };

  if (!editing) {
    const formatted =
      typeof mixIns?.formatDisplay === 'function'
        ? mixIns.formatDisplay(value, currency)
        : mixIns?.isAmount
        ? `${currencySymbolMap[currency] || currency || '$'}${Number(value || 0).toFixed(2)}`
        : value || '-';
    return (
      <span
        style={{ cursor: 'pointer', fontSize: '0.85rem', display: 'inline-block' }}
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
      >
        {formatted}
      </span>
    );
  }

  const handleChange = (e) => {
    const next = e.target.value;
    if (mixIns?.isAmount) {
      if (/^(\d+)?(\.\d{0,2})?$/.test(next) || next === '') {
        setValue(next);
      }
    } else {
      setValue(next);
    }
  };

  if (!mixIns?.isAmount) {
    return (
      <input
        type="text"
        value={value || ''}
        onChange={handleChange}
        onClick={(event) => event.stopPropagation()}
        onBlur={onSave}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave(e);
          if (e.key === 'Escape') {
            setValue(rowData?.[rawField] || rowData?.[field] || '');
            setEditing(false);
          }
        }}
        style={{ width: '100%', maxWidth: 200 }}
        autoFocus
      />
    );
  }

  const currencyOptions = mixIns?.currencyOptions || SUPPORTED_CURRENCIES;

  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: '6px', width: 'fit-content' }}
      onClick={(event) => event.stopPropagation()}
    >
      <select
        value={currency}
        onChange={(e) => {
          const next = e.target.value;
          setCurrency(next);
          saveAmount(next);
        }}
        data-currency-control="true"
        style={{ minWidth: 56, fontSize: '0.75rem' }}
      >
        {currencyOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <input
        type="text"
        value={value || ''}
        onChange={handleChange}
        onBlur={onSave}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave(e);
          if (e.key === 'Escape') {
            setValue(rowData?.[rawField] || rowData?.[field] || '');
            setEditing(false);
          }
        }}
        style={{ maxWidth: 100 }}
        autoFocus
      />
    </div>
  );
};

export default InlineTextCell;
